import { useT } from '@transifex/react';

import { PhoneField } from '#modules/ui/PhoneField';
import { TextArea } from '#modules/ui/TextArea';
import { TextField } from '#modules/ui/TextField';

export type FreeTextFieldProps = {
  value: string;
  /** SurveyJS `comment` — multiline. */
  isComment: boolean;
  /** `inputType: 'number'` (or a `number` question) — numeric keyboard + bounds check. */
  isNumeric: boolean;
  /** `inputType: 'tel'` — the masked `(xxx) xxx-xxxx` field. */
  isTel: boolean;
  numberError: string | null;
  phoneError: string | null;
  onChangeText: (text: string) => void;
  onNumberBlur: () => void;
  onPhoneBlur: () => void;
  testID?: string;
};

/**
 * Every typed-in question shape: a multiline comment, a masked phone number,
 * or a single-line field that may be numeric. One component rather than three
 * so the caller doesn't have to know which flag maps to which input — it just
 * hands over the question's flags.
 */
export function FreeTextField({
  value,
  isComment,
  isNumeric,
  isTel,
  numberError,
  phoneError,
  onChangeText,
  onNumberBlur,
  onPhoneBlur,
  testID,
}: FreeTextFieldProps) {
  const t = useT();

  if (isComment) {
    return (
      <TextArea
        value={value}
        onChangeText={onChangeText}
        placeholder={t('Type your answer')}
        testID={testID}
      />
    );
  }

  if (isTel) {
    return (
      <PhoneField
        value={value}
        onChangeText={onChangeText}
        onBlur={onPhoneBlur}
        placeholder={t('Type your answer')}
        errorMessage={phoneError ?? undefined}
        testID={testID}
      />
    );
  }

  return (
    <TextField
      value={value}
      onChangeText={onChangeText}
      onBlur={isNumeric ? onNumberBlur : undefined}
      placeholder={t('Type your answer')}
      inputMode={isNumeric ? 'numeric' : undefined}
      keyboardType={isNumeric ? 'number-pad' : undefined}
      errorMessage={isNumeric ? (numberError ?? undefined) : undefined}
      testID={testID}
    />
  );
}
